/**
 * Client for the RPC-over-POST API described in reference-note 01 §1.
 *
 * Every call is `POST /api/<namespace>.<verb>` with a JSON body, and every
 * response is the same envelope. This module is the only place in the client
 * that knows about the envelope shape; callers get `{data, policies, pagination}`
 * or an `RpcError`.
 */

import { EDITOR_VERSION } from "@wiki/core";

/** An entry of the envelope's `policies` array: abilities keyed by record id. */
export interface Policy {
  id: string;
  abilities: Record<string, boolean>;
}

export interface Pagination {
  offset: number;
  limit: number;
  total: number;
  nextPath?: string;
}

export interface RpcResult<T> {
  data: T;
  policies: Policy[];
  pagination: Pagination | undefined;
}

export interface RpcOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Extra request headers such as `x-api-version`. */
  apiVersion?: number;
  /** Set on the one retry that follows a `csrf_error`; never by callers. */
  csrfRetried?: boolean;
}

/**
 * Thrown for every non-success outcome: an `ok: false` envelope, a transport
 * failure, or a body that is not the envelope at all. `id` is the stable
 * snake_case identifier callers should branch on (note 01 §1.5); `message` is
 * prose and is only for display.
 */
export class RpcError extends Error {
  readonly id: string;
  readonly status: number;
  readonly data: unknown;

  constructor(id: string, status: number, message: string, data?: unknown) {
    super(message);
    this.name = "RpcError";
    this.id = id;
    this.status = status;
    this.data = data;
  }
}

/** Fired on `window` whenever the server rejects a call with 401. */
export const AUTH_EXPIRED_EVENT = "auth:expired";

/**
 * The double-submit CSRF cookie is host-only and readable by script. The server
 * names it `__Host-csrfToken` over TLS and `csrfToken` over plain HTTP, because
 * the `__Host-` prefix is not accepted without `Secure`
 * (`docs/spec/api-conventions.md` §11). The shorter spellings are accepted too,
 * so a server that trims the name does not silently break every mutation.
 */
const CSRF_COOKIE_NAMES = ["__Host-csrfToken", "csrfToken", "__Host-csrf", "csrf"] as const;

export function readCsrfToken(cookieString?: string): string | null {
  const source = cookieString ?? (typeof document === "undefined" ? "" : document.cookie);
  const jar = new Map<string, string>();
  for (const part of source.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const name = part.slice(0, eq).trim();
    if (name.length === 0) continue;
    jar.set(name, decodeURIComponent(part.slice(eq + 1).trim()));
  }
  for (const name of CSRF_COOKIE_NAMES) {
    const value = jar.get(name);
    if (value !== undefined && value !== "") return value;
  }
  return null;
}

interface Envelope {
  ok?: unknown;
  status?: unknown;
  data?: unknown;
  policies?: unknown;
  pagination?: unknown;
  error?: unknown;
  message?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asPolicies(value: unknown): Policy[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is Policy => isRecord(entry) && typeof entry.id === "string");
}

function asPagination(value: unknown): Pagination | undefined {
  if (!isRecord(value)) return undefined;
  const { offset, limit, total, nextPath } = value;
  if (typeof offset !== "number" || typeof limit !== "number" || typeof total !== "number") {
    return undefined;
  }
  return typeof nextPath === "string"
    ? { offset, limit, total, nextPath }
    : { offset, limit, total };
}

function announceAuthExpired(method: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { method } }));
}

/** Build the URL for a method. Exported so tests and links can reuse it. */
export function rpcUrl(method: string): string {
  return `/api/${method}`;
}

/** The cheapest route the server owns; any GET to it mints or replaces the CSRF cookie. */
export const CSRF_BOOTSTRAP_PATH = "/_health";

/**
 * Asks the server for a CSRF cookie. Used before the first mutation when none is readable,
 * and after a `csrf_error`, when the one we hold was signed by a key the server no longer
 * has (a restarted development server generates a new one every time).
 */
export async function refreshCsrfCookie(): Promise<void> {
  try {
    await fetch(CSRF_BOOTSTRAP_PATH, { credentials: "include" });
  } catch {
    // Offline, or the server is not up yet. The mutation that follows reports its own
    // failure; swallowing this one keeps the boot path quiet.
  }
}

export async function rpc<T>(
  method: string,
  body?: unknown,
  options: RpcOptions = {},
): Promise<RpcResult<T>> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "application/json",
    // The editor we are running. A server whose editor major is ahead of ours refuses our
    // writes with `400 editor_update_required` rather than letting an old client silently
    // drop the nodes it cannot represent (`docs/spec/api-conventions.md` §8).
    "x-editor-version": EDITOR_VERSION,
    ...options.headers,
  };

  const csrf = readCsrfToken();
  if (csrf !== null) {
    headers["x-csrf-token"] = csrf;
  }
  if (options.apiVersion !== undefined) {
    headers["x-api-version"] = String(options.apiVersion);
  }

  const init: RequestInit = {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(body ?? {}),
  };
  if (options.signal) {
    init.signal = options.signal;
  }

  let response: Response;
  try {
    response = await fetch(rpcUrl(method), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    const message = cause instanceof Error ? cause.message : "Network request failed";
    throw new RpcError("network_error", 0, message);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    if (response.status === 401) announceAuthExpired(method);
    throw new RpcError(
      response.ok ? "invalid_response" : "internal_error",
      response.status,
      `The server returned a response that was not JSON (${String(response.status)})`,
    );
  }

  if (!isRecord(payload)) {
    throw new RpcError("invalid_response", response.status, "Unexpected response body");
  }

  const envelope = payload as Envelope;
  const status = typeof envelope.status === "number" ? envelope.status : response.status;
  const ok = typeof envelope.ok === "boolean" ? envelope.ok : response.ok;

  if (!ok) {
    if (status === 401) announceAuthExpired(method);
    const id = typeof envelope.error === "string" ? envelope.error : "internal_error";
    // A refused CSRF pair means the cookie is stale, not that the user did anything wrong:
    // the refusal itself carries a fresh cookie, so one retry with it is the whole recovery.
    if (id === "csrf_error" && options.csrfRetried !== true) {
      await refreshCsrfCookie();
      return rpc<T>(method, body, { ...options, csrfRetried: true });
    }
    const message =
      typeof envelope.message === "string" && envelope.message !== ""
        ? envelope.message
        : `Request to ${method} failed`;
    throw new RpcError(id, status, message, envelope.data);
  }

  return {
    data: envelope.data as T,
    policies: asPolicies(envelope.policies),
    pagination: asPagination(envelope.pagination),
  };
}

/**
 * Some sign-in routes are plain HTTP endpoints rather than RPC methods
 * (`/auth/email`, `/auth/<provider>`; note 01 §4.3). They answer with a small
 * JSON object — `{success: true}`, `{redirect: "…"}`, or an error envelope — so
 * they get a sibling helper rather than being forced through `rpc`.
 */
export interface AuthPostResult {
  success: boolean;
  redirect?: string;
}

export async function postAuth(path: string, body: unknown): Promise<AuthPostResult> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "application/json",
  };
  const csrf = readCsrfToken();
  if (csrf !== null) headers["x-csrf-token"] = csrf;

  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    });
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : "Network request failed";
    throw new RpcError("network_error", 0, message);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  const envelope = isRecord(payload) ? payload : {};
  const ok = typeof envelope.ok === "boolean" ? envelope.ok : response.ok;
  if (!ok) {
    const id = typeof envelope.error === "string" ? envelope.error : "internal_error";
    const message =
      typeof envelope.message === "string" && envelope.message !== ""
        ? envelope.message
        : "Sign-in request failed";
    throw new RpcError(id, response.status, message, envelope.data);
  }

  const inner = isRecord(envelope.data) ? envelope.data : envelope;
  const redirect = inner.redirect;
  return {
    success: inner.success !== false,
    ...(typeof redirect === "string" ? { redirect } : {}),
  };
}
